import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import {
  type AdminUnifiedEvaluationItem,
  useAdminEvaluationsQuery,
} from '../../../features/admin/hooks/use-admin-api'

type SearchParams = { page?: number; search?: string }

export const Route = createFileRoute('/admin/evaluations/')({
  component: AdminEvaluationsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

function formatDate(date: string | Date | null): string {
  if (!date) return '\u2014'
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function scoreColor(score: number | null): string {
  if (score === null) return 'text-muted-foreground'
  if (score >= 75) return 'text-emerald-400'
  if (score >= 50) return 'text-amber-400'
  return 'text-red-400'
}

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'destructive'> = {
  COMPLETED: 'success',
  PROCESSING: 'warning',
  PENDING: 'secondary',
  ACTIVE: 'warning',
  IN_PROGRESS: 'warning',
  INCOMPLETE: 'warning',
  FAILED: 'destructive',
}

const SOURCE_LABEL: Record<string, string> = {
  pitch_session: 'Pitch',
  prescreen: 'Prescreen',
  prescreen_request: 'Prescreen Req',
}

const columns: TableColumn<AdminUnifiedEvaluationItem>[] = [
  {
    header: 'ID',
    width: '80px',
    render: (e) =>
      e.source === 'prescreen_request' ? (
        <span className="font-mono text-xs text-muted-foreground">{e.id.slice(0, 8)}</span>
      ) : (
        <Link
          to="/admin/evaluations/$id"
          params={{ id: e.id }}
          className="font-mono text-xs text-primary hover:underline"
        >
          {e.id.slice(0, 8)}
        </Link>
      ),
  },
  {
    header: 'Source',
    width: '140px',
    render: (e) => (
      <Badge
        variant={e.source === 'pitch_session' ? 'success' : 'purple'}
        size="sm"
        className="uppercase"
      >
        {SOURCE_LABEL[e.source] ?? e.source}
      </Badge>
    ),
  },
  {
    header: 'Startup',
    render: (e) =>
      e.startupId ? (
        <Link
          to="/admin/startups/$id"
          params={{ id: e.startupId }}
          className="font-medium text-primary hover:underline"
        >
          {e.startupName ?? 'Unknown'}
        </Link>
      ) : (
        <CellText>{e.startupName ?? '\u2014'}</CellText>
      ),
  },
  {
    header: 'Investor',
    render: (e) =>
      e.investorId ? (
        <Link
          to="/admin/investors/$id"
          params={{ id: e.investorId }}
          className="text-primary hover:underline"
        >
          {e.investorName}
        </Link>
      ) : (
        <CellText>{e.investorName ?? '\u2014'}</CellText>
      ),
  },
  {
    header: 'Status',
    width: '110px',
    render: (e) => (
      <Badge variant={STATUS_VARIANT[e.status] ?? 'secondary'} size="sm">
        {e.status}
      </Badge>
    ),
  },
  {
    header: 'Score',
    width: '70px',
    render: (e) => (
      <span className={`tabular-nums text-sm font-medium ${scoreColor(e.score)}`}>
        {e.score ?? '\u2014'}
      </span>
    ),
  },
  {
    header: 'Created',
    width: '200px',
    render: (e) => <CellText>{formatDate(e.createdAt)}</CellText>,
  },
]

function AdminEvaluationsPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const query = useAdminEvaluationsQuery({ page, limit: LIMIT, search })
  const queryData = query.data as
    | { data?: { items?: AdminUnifiedEvaluationItem[]; total?: number } }
    | undefined
  const items = queryData?.data?.items ?? []
  const total = queryData?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-4">
      <SearchInput
        placeholder="Search by startup name..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={items}
        isLoading={query.isLoading}
        emptyMessage="No evaluations found."
        keyExtractor={(e) => e.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
